// LES EQUACIONS: com s'escriuen, com es desen i com es calculen.
//
// Fora de React i sense cap import, com la resta de peces que es poden provar soles.
//
// ── EL PROBLEMA DE FONS: LES REFERÈNCIES PER NOM ─────────────────────────────────────────────
//
// Una equació de flux diu coses com `Dipòsit * Taxa de buidatge`: **anomena altres components**.
// Fer-ho per nom és el que fan Vensim i Stella i és el que la gent espera escriure, però desar-ho
// per nom és el parany que en aquest projecte ja ha picat tres vegades —`parseInt(c.id)` als
// components, el prefix `rel_` de les relacions, i els extrems de les fletxes—:
//
// > **Qui assigna l'identificador és qui l'ha de propagar.**
//
// Reanomenar un component —que el llenç deixa fer amb doble clic— trencaria totes les equacions
// que l'anomenessin, i en silenci: o deixarien de resoldre, o **pitjor encara, seguirien
// resolent** si el nom vell fos prefix d'un altre component.
//
// Per això aquí la referència es desa per IDENTIFICADOR i s'ensenya per nom:
//
//     el que s'escriu     Dipòsit * Taxa de buidatge
//     el que es desa      {{22222…-000}} * {{22222…-003}}
//     el que es torna a
//     ensenyar            Dipòsit * Taxa de buidatge          ← encara que s'hagi rebatejat
//
// Els dobles claudàtors no poden sortir d'un nom perquè el nom no els pot contenir: es netegen
// en entrar. La conversió es fa en els dos sentits i es prova als dos sentits.
//
// ── EL RECONEIXEMENT DELS NOMS ───────────────────────────────────────────────────────────────
//
// **Coincidència més llarga primer**, que és el que permet que un nom porti espais sense haver-lo
// d'encerclar amb res. Amb `Taxa` i `Taxa de buidatge` tots dos al model, escriure la segona no
// es llegeix mai com la primera seguida de text solt.
//
// **I amb frontera de token als dos costats**, que la coincidència més llarga sola no salva. Un
// component que es digui `a` —perfectament legítim— es ficava dins de qualsevol paraula:
//
//     amb un component «a»:   `Cabal * 2`  →  `C{{a}}b{{a}}l * 2`
//
// Longest-match protegeix un nom conegut d'un altre nom conegut més curt; **no protegeix res
// d'una paraula que qui escriu encara no ha acabat de teclejar**, i l'inspector fa aquesta
// conversió a cada tecla. La condició és que ni el caràcter de davant ni el de darrere siguin
// lletra o número.

const OPERADORS = { '+': 1, '-': 1, '*': 2, '/': 2 };

// ── EL TEMPS ─────────────────────────────────────────────────────────────────────────────────
//
// L'única referència que no és cap component. Es desa com `{{@t}}` i s'ensenya amb la paraula de
// l'idioma que toqui —el mateix motiu pel qual les altres referències es desen per identificador:
// si es desés la paraula, canviar d'idioma deixaria anglès dins de les equacions—.
//
// **Es reconeixen sempre les dues paraules**, escrigui's en l'idioma que s'escrigui, perquè
// canviar d'idioma a mitja frase no ha de trencar el que s'estava escrivint.
export const REF_TEMPS = '@t';
export const PARAULA_TEMPS = { en: 'TIME', ca: 'TEMPS' };
const TOTES_TEMPS = Object.values(PARAULA_TEMPS);
export const esNomReservat = (n) =>
  TOTES_TEMPS.some((p) => p.toLowerCase() === String(n || '').trim().toLowerCase());
export const REF = /^\{\{([^}]+)\}\}$/;

// El nom es neteja: sense claudàtors dobles ni res que pugui semblar una referència.
export const nomNet = (n) => String(n || '').replace(/[{}]/g, '').trim();

// ── ESCRIURE → DESAR ─────────────────────────────────────────────────────────────────────────

export function nomsCapAIds(text, components) {
  const candidats = [
    ...TOTES_TEMPS.map((nom) => ({ nom, id: REF_TEMPS })),
    ...components.filter((c) => nomNet(c.name)).map((c) => ({ nom: nomNet(c.name), id: String(c.id) })),
  ].sort((a, b) => b.nom.length - a.nom.length);   // els llargs primer

  // Lletra o número de qualsevol alfabet: els noms porten accents i ce trencades.
  const lletra = (ch) => ch !== undefined && /[\p{L}\p{N}_]/u.test(ch);

  let sortida = '', i = 0;
  const t = String(text || '');
  while (i < t.length) {
    let trobat = null;
    for (const c of candidats) {
      if (!t.startsWith(c.nom, i)) continue;
      // Frontera als dos costats: `a` dins de `Cabal` no és el component `a`.
      if (lletra(t[i - 1]) || lletra(t[i + c.nom.length])) continue;
      trobat = c; break;
    }
    if (trobat) { sortida += `{{${trobat.id}}}`; i += trobat.nom.length; }
    else { sortida += t[i]; i += 1; }
  }
  return sortida;
}

// ── DESAR → ENSENYAR ─────────────────────────────────────────────────────────────────────────

export function idsCapANoms(text, components, idioma = 'en') {
  const perId = new Map(components.map((c) => [String(c.id), nomNet(c.name)]));
  perId.set(REF_TEMPS, PARAULA_TEMPS[idioma] || PARAULA_TEMPS.en);
  // Un component esborrat deixa la referència penjada. S'ensenya perquè es vegi que hi és i que
  // s'ha d'arreglar, en comptes de fer-la desaparèixer i deixar una equació que no quadra.
  return String(text || '').replace(/\{\{([^}]+)\}\}/g, (_, id) => perId.get(id) || '?');
}

// ── ANALITZAR ────────────────────────────────────────────────────────────────────────────────
//
// Un analitzador de precedència, prou per a `+ - * /`, parèntesis i el menys unari. Deliberadament
// no hi ha funcions ni condicionals: cadascuna que s'hi afegeixi és una porta a expressions no
// derivables, i llavors la tria d'integrador deixa de ser innocent. Si mai calen, que sigui una
// decisió i no una addició.

function tokenitza(text) {
  const tokens = [];
  const t = String(text || '');
  let i = 0;
  while (i < t.length) {
    const c = t[i];
    if (/\s/.test(c)) { i += 1; continue; }
    if (c === '{' && t[i + 1] === '{') {
      const fi = t.indexOf('}}', i);
      if (fi < 0) return { error: 'sf.eqRefOberta' };
      tokens.push({ mena: 'ref', valor: t.slice(i + 2, fi) });
      i = fi + 2; continue;
    }
    if (/[0-9.]/.test(c)) {
      let j = i;
      while (j < t.length && /[0-9.]/.test(t[j])) j += 1;
      const n = Number(t.slice(i, j));
      if (!Number.isFinite(n)) return { error: 'sf.eqNumeroDolent' };
      tokens.push({ mena: 'num', valor: n });
      i = j; continue;
    }
    if (c in OPERADORS || c === '(' || c === ')') { tokens.push({ mena: c }); i += 1; continue; }
    // Qualsevol altra cosa és un nom que no s'ha reconegut: es diu quin, no «error de sintaxi».
    let j = i;
    while (j < t.length && !/[\s()+\-*/]/.test(t[j]) && t[j] !== '{') j += 1;
    return { error: 'sf.eqNomDesconegut', detall: t.slice(i, j || i + 1) };
  }
  return { tokens };
}

export function analitza(text) {
  if (!String(text || '').trim()) return { arbre: { mena: 'num', valor: 0 } };
  const r = tokenitza(text);
  if (r.error) return r;
  const tks = r.tokens;
  let p = 0;

  const primari = () => {
    const tk = tks[p];
    if (!tk) return { error: 'sf.eqIncompleta' };
    if (tk.mena === 'num' || tk.mena === 'ref') { p += 1; return { arbre: tk }; }
    if (tk.mena === '-') { p += 1; const d = primari(); return d.error ? d : { arbre: { mena: 'neg', d: d.arbre } }; }
    if (tk.mena === '+') { p += 1; return primari(); }
    if (tk.mena === '(') {
      p += 1;
      const dins = expressio(1);
      if (dins.error) return dins;
      if (tks[p]?.mena !== ')') return { error: 'sf.eqParentesi' };
      p += 1; return dins;
    }
    return { error: 'sf.eqIncompleta' };
  };

  const expressio = (minima) => {
    let esq = primari();
    if (esq.error) return esq;
    while (tks[p] && tks[p].mena in OPERADORS && OPERADORS[tks[p].mena] >= minima) {
      const op = tks[p].mena; p += 1;
      const dre = expressio(OPERADORS[op] + 1);
      if (dre.error) return dre;
      esq = { arbre: { mena: 'op', op, e: esq.arbre, d: dre.arbre } };
    }
    return esq;
  };

  const res = expressio(1);
  if (res.error) return res;
  if (p < tks.length) return { error: 'sf.eqSobra' };
  return res;
}

export function referencies(arbre, acumula = new Set()) {
  if (!arbre) return acumula;
  if (arbre.mena === 'ref') acumula.add(arbre.valor);
  if (arbre.e) referencies(arbre.e, acumula);
  if (arbre.d) referencies(arbre.d, acumula);
  return acumula;
}

export function avalua(arbre, valors) {
  switch (arbre.mena) {
    case 'num': return arbre.valor;
    case 'ref': return Number(valors[arbre.valor]) || 0;
    case 'neg': return -avalua(arbre.d, valors);
    case 'op': {
      const a = avalua(arbre.e, valors), b = avalua(arbre.d, valors);
      if (arbre.op === '+') return a + b;
      if (arbre.op === '-') return a - b;
      if (arbre.op === '*') return a * b;
      // Dividir per zero dona Infinity i contamina tot el model d'un pas per l'altre. Val més
      // que doni zero i que la conducta plana es vegi, que no pas NaN a tot arreu.
      return b === 0 ? 0 : a / b;
    }
    default: return 0;
  }
}

const EXPRESSIONS = { nomsCapAIds, idsCapANoms, analitza, avalua, referencies, nomNet, REF,
                      REF_TEMPS, PARAULA_TEMPS, esNomReservat };
export default EXPRESSIONS;
